import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Search, MapPin, Calendar, Eye, Trash2, Copy, FileText, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ItinerariesList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [itineraries, setItineraries] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/itineraries`);
      setItineraries(response.data);
    } catch (error) {
      toast.error('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this itinerary?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/itineraries/${id}`);
      toast.success('Itinerary deleted');
      fetchItineraries();
    } catch (error) {
      toast.error('Failed to delete itinerary');
    }
  };

  const duplicateItinerary = async (itinerary) => {
    try {
      // Bug fix (2026-10-06): this used to blank tripId before saving the
      // copy, which made the backend run its "auto-create Trip + Traveller"
      // fallback (meant only for a client opening a brand-new public link
      // for the first time) — minting a brand new Trip + new client on
      // every duplication. Keeping the original tripId links the new copy
      // to the SAME trip/client; the trip's "active itinerary" pointer just
      // moves to this new copy, no orphan trip or duplicate client created.
      const newItinerary = {
        ...itinerary,
        title: `${itinerary.title} (Copy)`,
        tripId: itinerary.tripId || ''
      };
      delete newItinerary.id;
      delete newItinerary.createdAt;
      delete newItinerary.updatedAt;
      
      await axios.post(`${BACKEND_URL}/api/itineraries`, newItinerary);
      toast.success('Itinerary duplicated!');
      fetchItineraries();
    } catch (error) {
      toast.error('Failed to duplicate itinerary');
    }
  };

  const createFromTemplate = async (itinerary) => {
    // Navigate to builder with template ID - builder will fetch and pre-fill all data
    navigate(`/itineraries/new?template=${itinerary.id}`);
    toast.success('Opening template for customization...');
  };

  const [filterType, setFilterType] = useState('ready');

  const filtered = itineraries.filter(i => {
    const matchesSearch = i.title.toLowerCase().includes(search.toLowerCase()) ||
      i.destination.toLowerCase().includes(search.toLowerCase());
    // Treat legacy "template" as "ready" so old data still matches the new filter.
    const pkgType = (i.packageType === 'template' ? 'ready' : (i.packageType || 'custom'));
    const matchesType = filterType === 'all' || pkgType === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-3">
              Ready Packages
            </h1>
            <p className="text-base lg:text-lg text-slate-600 font-light">
              {filtered.length} {filterType === 'all' ? 'itinerary packages' : filterType === 'ready' ? 'ready-made packages' : 'custom packages'}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/itineraries/new')} 
            data-testid="create-itinerary-btn"
            className="bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-lg px-6 py-3 h-12 rounded-xl font-semibold"
          >
            <Plus className="h-5 w-5 mr-2" />Create Itinerary
          </Button>
        </div>
      </div>

      {/* Search & Filter */}
      <Card className="p-4 bg-white/80 backdrop-blur-xl rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search itineraries..."
              className="pl-12 h-12 rounded-xl"
              data-testid="search-itineraries"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setFilterType('all')} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filterType === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="filter-all">All</button>
            <button onClick={() => setFilterType('ready')} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filterType === 'ready' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="filter-templates">Ready Packages</button>
            <button onClick={() => setFilterType('custom')} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filterType === 'custom' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="filter-custom">Custom</button>
          </div>
        </div>
      </Card>

      {/* Itineraries Grid */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center bg-white/80 backdrop-blur-xl rounded-3xl">
          <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-heading font-bold text-slate-900 mb-2">No itineraries found</h3>
          <p className="text-slate-600 mb-6">Create your first itinerary template to get started</p>
          <Button onClick={() => navigate('/itineraries/new')} className="bg-primary-700 text-white rounded-xl">
            <Plus className="h-4 w-4 mr-2" />Create Itinerary
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((itinerary) => (
            <Card 
              key={itinerary.id}
              className="p-6 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl hover:shadow-xl transition-all cursor-pointer group"
              onClick={() => navigate(`/itineraries/${itinerary.id}`)}
              data-testid={`itinerary-card-${itinerary.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 rounded-2xl flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-primary-700" />
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                    ((itinerary.packageType === 'template' ? 'ready' : (itinerary.packageType || 'custom'))) === 'ready'
                      ? 'bg-teal-100 text-teal-700 border border-teal-200'
                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                  }`}>
                    {((itinerary.packageType === 'template' ? 'ready' : (itinerary.packageType || 'custom'))) === 'ready' ? 'Ready' : 'Custom'}
                  </span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {((itinerary.packageType === 'template' ? 'ready' : (itinerary.packageType || 'custom'))) === 'ready' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); createFromTemplate(itinerary); }}
                      className="h-8 px-2 text-teal-600 text-xs font-semibold"
                      title="Use This Template"
                      data-testid={`use-template-${itinerary.id}`}
                    >
                      <Package className="h-3.5 w-3.5 mr-1" />Use
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); duplicateItinerary(itinerary); }}
                    className="h-8 w-8 p-0 text-slate-500"
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleDelete(itinerary.id); }}
                    className="h-8 w-8 p-0 text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 line-clamp-1">{itinerary.title}</h3>
              
              <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />{itinerary.destination}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />{itinerary.duration} Days
                </span>
              </div>

              {itinerary.tripId && (
                <div className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded-lg inline-block">
                  Linked: {itinerary.displayTripId || itinerary.tripId}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                <span className="text-xs text-slate-400">
                  {itinerary.inclusions?.length || 0} inclusions • {itinerary.exclusions?.length || 0} exclusions
                </span>
                <Eye className="h-4 w-4 text-slate-400 group-hover:text-primary-600 transition-colors" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ItinerariesList;
